import { useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { Sparkles, Brain, ChevronRight, Check, X, Loader2, RotateCcw, Lock, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { generateScoreCardBlob, shareOrDownload } from "../lib/scoreCard";
import { useAuth } from "../context/AuthContext";

export default function Quiz() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [cats, setCats] = useState([]);
  const [categoryId, setCategoryId] = useState(params.get("category_id") || "");
  const [count, setCount] = useState(Number(params.get("n")) || 5);
  const [usage, setUsage] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({});
  const [revealed, setRevealed] = useState(false);
  const autoRef = useRef(false);

  const loadUsage = async () => {
    const u = await api.get("/quiz/usage");
    setUsage(u.data);
  };

  useEffect(() => {
    (async () => {
      const [c, u] = await Promise.all([api.get("/categories"), api.get("/quiz/usage")]);
      setCats(c.data); setUsage(u.data);
      // Auto-trigger generation when navigated from a reminder/test card
      if (params.get("auto") === "1" && !autoRef.current) {
        autoRef.current = true;
        const limited = !u.data.unlimited && u.data.used_today >= (u.data.daily_limit || 0);
        if (limited) {
          toast.error("Free plan: 1 quiz per day. Upgrade for unlimited.", { duration: 5000 });
        } else {
          // small delay so cats render first
          setTimeout(() => generate(), 200);
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const generate = async () => {
    setBusy(true); setQuestions([]); setAnswers({}); setRevealed(false);
    try {
      const r = await api.post("/quiz/generate", { count, category_id: categoryId || null });
      setQuestions(r.data.questions);
      setUsage({ plan: r.data.plan, used_today: r.data.used_today, daily_limit: r.data.daily_limit, unlimited: r.data.unlimited });
      toast.success(`Generated ${r.data.questions.length} questions`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
      loadUsage();
    } finally { setBusy(false); }
  };

  const reset = () => { setQuestions([]); setAnswers({}); setRevealed(false); };

  const submit = async () => {
    setRevealed(true);
    // Persist results to word_stats so weak-spots & the digest reflect this attempt.
    try {
      const results = questions.map((q, i) => ({
        term: q.term || "",
        correct: answers[i] === q.correct_index,
      })).filter((r) => r.term);
      if (results.length > 0) {
        await api.post("/quiz/submit", { results });
      }
    } catch (e) {
      // non-blocking — user still sees their score even if save fails
      console.warn("quiz submit save failed", e);
    }
    // Scroll to top so user sees their score / share card
    try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch { /* ignore */ }
  };

  const score = revealed
    ? questions.reduce((acc, q, i) => acc + (answers[i] === q.correct_index ? 1 : 0), 0)
    : null;

  const limitHit = usage && !usage.unlimited && usage.used_today >= (usage.daily_limit || 0);

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-4xl">
      <header className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Quiz me</p>
        <h1 className="font-display text-4xl font-black mt-1">MCQ generator</h1>
        <p className="text-base font-medium text-slate-600 mt-2">
          Generate a multiple-choice quiz from your glossary. Test your retention before exams.
        </p>
      </header>

      <div className="nb-card p-5 mb-6 grid sm:grid-cols-[1fr_auto] gap-4 items-center" style={{ background: "#86EFAC33" }}>
        <div className="text-sm font-bold" data-testid="quiz-usage">
          {usage ? (
            usage.unlimited
              ? <>✨ <strong>Unlimited</strong> quizzes — you're on {usage.plan === "pro_plus" ? "Pro Plus" : "Pro"}.</>
              : <>You've used <strong>{usage.used_today}</strong> of <strong>{usage.daily_limit}</strong> free generation today. Upgrade for unlimited.</>
          ) : "Loading…"}
        </div>
        {usage && !usage.unlimited && (
          <Link to="/pricing" className="nb-btn-primary inline-flex items-center gap-2"><Sparkles className="w-4 h-4" strokeWidth={2.5}/> Upgrade</Link>
        )}
      </div>

      {questions.length === 0 ? (
        <div className="nb-card p-6">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2">
            <Brain className="w-5 h-5" strokeWidth={2.5}/> Set up your quiz
          </h2>
          <div className="mt-4 grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">Category</label>
              <select data-testid="quiz-category" value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className="nb-input mt-1">
                <option value="">All categories</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase font-bold tracking-widest">Questions</label>
              <select data-testid="quiz-count" value={count} onChange={(e) => setCount(Number(e.target.value))} className="nb-input mt-1">
                {[5, 10, 15].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={generate}
                disabled={busy || limitHit}
                data-testid="quiz-generate-btn"
                className="nb-btn-primary w-full inline-flex items-center justify-center gap-2"
              >
                {limitHit ? <Lock className="w-4 h-4" strokeWidth={2.5}/> : busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5}/> : <Sparkles className="w-4 h-4" strokeWidth={2.5}/>}
                {limitHit ? "Limit reached" : busy ? "Generating…" : "Generate"}
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-500 mt-4">Tip: add at least 4 words to your glossary first.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {questions.map((q, i) => {
            const picked = answers[i];
            return (
              <article key={i} className="nb-card p-5" data-testid={`quiz-q-${i}`}>
                <div className="flex items-start gap-3">
                  <span className="nb-tag bg-yellow-300">Q{i + 1}</span>
                  <h3 className="font-display text-lg font-bold flex-1">{q.question}</h3>
                </div>
                <div className="mt-4 space-y-2">
                  {q.options.map((opt, oi) => {
                    const isPicked = picked === oi;
                    const isCorrect = q.correct_index === oi;
                    let cls = "border-slate-900 bg-white";
                    if (revealed) {
                      if (isCorrect) cls = "border-slate-900 bg-green-200";
                      else if (isPicked) cls = "border-slate-900 bg-pink-200";
                    } else if (isPicked) {
                      cls = "border-slate-900 bg-yellow-200";
                    }
                    return (
                      <button
                        key={oi}
                        type="button"
                        disabled={revealed}
                        onClick={() => setAnswers((a) => ({ ...a, [i]: oi }))}
                        data-testid={`quiz-q-${i}-opt-${oi}`}
                        className={`w-full text-left flex items-center gap-3 px-4 py-3 border-2 rounded-md font-medium transition-colors ${cls}`}
                        style={{ boxShadow: "2px 2px 0 0 #0F172A" }}
                      >
                        <span className="font-display font-black text-sm w-6">{String.fromCharCode(65 + oi)}.</span>
                        <span className="flex-1">{opt}</span>
                        {revealed && isCorrect && <Check className="w-5 h-5" strokeWidth={3}/>}
                        {revealed && isPicked && !isCorrect && <X className="w-5 h-5" strokeWidth={3}/>}
                      </button>
                    );
                  })}
                </div>
                {revealed && q.term && (
                  <p className="text-xs font-medium text-slate-500 mt-3">From your glossary: <span className="font-bold text-slate-800">{q.term}</span></p>
                )}
                {revealed && q.explanation && (
                  <div className="mt-3 nb-card-sm p-3" style={{ background: "#FDE04733" }}>
                    <p className="text-sm font-medium"><strong>Why:</strong> {q.explanation}</p>
                  </div>
                )}
              </article>
            );
          })}

          <div className="nb-card p-5 flex flex-wrap items-center justify-between gap-4">
            {revealed ? (
              <>
                <div className="font-display text-2xl font-black">
                  Score: <span data-testid="quiz-score">{score} / {questions.length}</span>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    onClick={async () => {
                      try {
                        const wrong = questions.find((q, i) => answers[i] !== q.correct_index);
                        const topic = categoryId ? cats.find((c) => c.id === categoryId)?.name : "my glossary";
                        const blob = await generateScoreCardBlob({
                          score, total: questions.length,
                          topic: topic || null,
                          weakWord: wrong?.term || null,
                          name: user?.name?.split(" ")[0] || null,
                        });
                        const result = await shareOrDownload(blob, `studict-score-${score}-of-${questions.length}.png`,
                          `I scored ${score}/${questions.length} on Studict! 📚`);
                        if (result === "downloaded") toast.success("Score card downloaded");
                      } catch (e) {
                        toast.error("Could not create share card");
                      }
                    }}
                    data-testid="quiz-share-btn"
                    className="nb-btn-primary inline-flex items-center gap-2"
                  >
                    <Share2 className="w-4 h-4" strokeWidth={2.5}/> Share my score
                  </button>
                  <button onClick={reset} data-testid="quiz-reset-btn" className="nb-btn-secondary inline-flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" strokeWidth={2.5}/> New quiz
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-slate-700">
                  Answered {Object.keys(answers).length} / {questions.length}
                </p>
                <div className="flex gap-3">
                  <button onClick={reset} className="nb-btn-secondary">Cancel</button>
                  <button
                    onClick={submit}
                    disabled={Object.keys(answers).length < questions.length}
                    data-testid="quiz-submit-btn"
                    className="nb-btn-primary inline-flex items-center gap-2"
                  >
                    Submit <ChevronRight className="w-4 h-4" strokeWidth={2.5}/>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
